import { useCallback, useEffect, useRef, useState } from 'react'
import { jetonCapture, televerserMedia } from '../apiCatalogue'
import { useTextes } from '../LangueContexte'
import { interpoler } from '../i18n'
import { Bandeau } from './Champs'
import { Bloc, Bouton, Icone, Image, Puce } from '../ui'

/**
 * Capture in-app — **le seul chemin qui vaut le badge** (D8).
 *
 * `MediaDevices.getUserMedia()` → `<canvas>` → `toBlob()`, et rien d'autre. Le tableau de D8 est
 * explicite sur ce que vaut l'alternative :
 *
 * | Chemin | Vaut le badge ? |
 * |---|---|
 * | `<input type="file" capture="environment">` | **Non.** Sélecteur de fichier sur desktop, comportement dépendant de l'OS sur mobile, EXIF de l'appareil conservé — indiscernable d'une galerie. |
 * | `getUserMedia` → canvas → `toBlob` | **Oui.** Le flux est capté dans la page, l'image est ré-encodée par le canvas donc **structurellement sans EXIF**, et le téléversement se lie au jeton émis juste avant l'ouverture du flux. |
 *
 * **Le jeton est demandé AVANT d'ouvrir la caméra**, et pas après la prise de vue : c'est ce qui
 * permet au serveur de mesurer le délai jeton → téléversement, et d'écarter ce qui n'a pas pu être
 * photographié — un fichier qui arrive 300 ms après n'a pas été cadré, il a été préparé.
 *
 * <b>Revu le 15/09/2026 sur la maquette `docs/ui/pwa/capture.html`</b> : un viseur 3:2 et rien
 * d'autre à l'écran, les trois commandes en dessous — changer de caméra, déclencher, terminer —,
 * les prises de la session en grille, et le rappel de l'invariant 8 au moment du geste. La
 * session reste ouverte après une photo : on photographie une chambre en plusieurs fois, et
 * refermer le viseur à chaque prise faisait rouvrir la caméra autant de fois.
 *
 * **Ce composant ne déclare jamais la provenance de l'image.** Il transmet un jeton ; le serveur
 * tranche. Ce code est téléchargé chez l'utilisateur : tout ce qu'il affirmerait serait sans
 * valeur, et l'invariant 7 en dépendrait.
 */
export function CaptureInApp({
  listingId,
  onCapture,
  onFermer,
}: {
  listingId: string | null
  onCapture: (mediaId: string) => void
  onFermer: () => void
}) {
  const t = useTextes()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const fluxRef = useRef<MediaStream | null>(null)

  const [pret, setPret] = useState(false)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  /** La caméra demandée : arrière d'abord, l'autre au geste. */
  const [face, setFace] = useState<'environment' | 'user'>('environment')
  /** Les prises de cette session, dans l'ordre : elles restent visibles tant que le viseur est ouvert. */
  const [prises, setPrises] = useState<string[]>([])

  const arreter = useCallback(() => {
    fluxRef.current?.getTracks().forEach((piste) => piste.stop())
    fluxRef.current = null
  }, [])

  useEffect(() => {
    let annule = false

    async function ouvrir() {
      try {
        const flux = await navigator.mediaDevices.getUserMedia({
          // `environment` : la caméra arrière sur un téléphone. Le navigateur choisit seul si
          // l'appareil n'en a qu'une, et la demande reste satisfaite.
          video: { facingMode: face, width: { ideal: 1920 }, height: { ideal: 1440 } },
          audio: false,
        })

        if (annule) {
          flux.getTracks().forEach((piste) => piste.stop())
          return
        }

        fluxRef.current = flux

        if (videoRef.current !== null) {
          videoRef.current.srcObject = flux
          await videoRef.current.play()
        }

        setPret(true)
      } catch {
        // Permission refusée, pas de caméra, contexte non sécurisé : l'utilisateur passe par la
        // galerie. Le badge n'est pas obtenu — la capture est incitative, pas obligatoire
        // (spec 5.2b) — et rien n'est bloqué.
        setErreur('camera_indisponible')
      }
    }

    setPret(false)
    void ouvrir()

    return () => {
      annule = true
      arreter()
    }
  }, [arreter, face])

  const declencher = useCallback(async () => {
    const video = videoRef.current

    if (video === null || occupe) {
      return
    }

    setOccupe(true)
    setErreur(null)

    try {
      // Le jeton part AVANT tout le reste de la séquence de prise de vue côté serveur : la
      // séquence complète est jeton → capture → téléversement, et son ordre est vérifié.
      const jeton = await jetonCapture(listingId)

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      const contexte = canvas.getContext('2d')

      if (contexte === null) {
        setErreur('canvas_indisponible')
        return
      }

      contexte.drawImage(video, 0, 0, canvas.width, canvas.height)

      // C'est ici que l'image perd toute métadonnée : un canvas ré-encode des pixels, il ne
      // transporte aucun EXIF. La propriété n'est pas un réglage, c'est une conséquence du chemin.
      const blob = await new Promise<Blob | null>((resoudre) =>
        canvas.toBlob(resoudre, 'image/jpeg', 0.92),
      )

      if (blob === null) {
        setErreur('encodage_impossible')
        return
      }

      const { mediaId } = await televerserMedia(blob, 'capture.jpg', listingId, jeton.tokenId)

      setPrises((actuelles) => [...actuelles, mediaId])
      onCapture(mediaId)
    } catch {
      setErreur('televersement_impossible')
    } finally {
      setOccupe(false)
    }
  }, [listingId, occupe, onCapture])

  if (erreur === 'camera_indisponible') {
    return (
      <div className="capture">
        <Bandeau ton="attention" icone={<Icone nom="appareil" />}>
          {t.captureIndisponible}
        </Bandeau>
        <div>
          <Bouton apparence="secondaire" onClick={onFermer}>
            {t.fermer}
          </Bouton>
        </div>
      </div>
    )
  }

  return (
    <div className="capture">
      <div className="page-entete capture__entete">
        <h3>{t.captureTitre}</h3>
        <p>{t.captureAide}</p>
      </div>

      {/* Le viseur : un cadre 3:2, le flux dedans, un cadre pointillé et une consigne, rien d'autre. */}
      <div className="capture__viseur">
        <video ref={videoRef} className="capture__flux" playsInline muted />
        <div className="capture__cadre" aria-hidden="true" />
        <span className="puce capture__consigne">{t.captureConsigne}</span>
      </div>

      <div className="capture__commandes">
        <button
          type="button"
          className="entete__icone"
          aria-label={t.captureChangerCamera}
          disabled={occupe}
          onClick={() => setFace((courante) => (courante === 'environment' ? 'user' : 'environment'))}
        >
          <Icone nom="rafraichir" taille="lg" />
        </button>
        <button
          type="button"
          className="capture__declencheur"
          aria-label={occupe ? t.captureEnvoi : t.publicationPhotographier}
          onClick={() => void declencher()}
          disabled={!pret || occupe}
        />
        <button type="button" className="entete__icone" aria-label={t.captureTerminer} disabled={occupe} onClick={onFermer}>
          <Icone nom="coche" taille="lg" />
        </button>
      </div>

      {erreur !== null && erreur !== 'camera_indisponible' && (
        <Bandeau ton="erreur">{t.captureEchec}</Bandeau>
      )}

      <Bloc titre={<h3>{t.capturePrises}</h3>} actions={<Puce>{prises.length}</Puce>}>
        {prises.length === 0 ? (
          <p className="secondaire">{t.capturePrisesVide}</p>
        ) : (
          <div className="capture__prises">
            {prises.map((mediaId) => (
              <Image key={mediaId} mediaId={mediaId} alt="" ratio="1-1" sizes="7rem" />
            ))}
          </div>
        )}
      </Bloc>

      <Bandeau ton="erreur" icone={<Icone nom="bouclier" />}>
        <b>{t.captureAucunDocumentTitre}</b> {t.captureAucunDocument}
      </Bandeau>
    </div>
  )
}

/**
 * Téléversement depuis la galerie — **publication normale, sans badge**.
 *
 * « Une annonce avec des photos de la galerie est publiée normalement ; une annonce avec des photos
 * captées depuis l'application gagne un badge, une meilleure visibilité et un meilleur classement.
 * On récompense au lieu d'interdire » (spec 5.2b). Ce chemin ne transmet aucun jeton : il ne
 * prétend rien, et le serveur n'a rien à arbitrer.
 */
export function ChoixGalerie({
  listingId,
  onCapture,
}: {
  listingId: string | null
  onCapture: (mediaId: string) => void
}) {
  const t = useTextes()
  const [envoyes, setEnvoyes] = useState<number | null>(null)
  const [total, setTotal] = useState(0)
  const [echecs, setEchecs] = useState(0)

  const occupe = envoyes !== null

  return (
    <>
      {/* Un `<label>` qui porte les classes du bouton : le champ natif reste masqué, l'accessibilité
          du label est conservée, et la galerie se présente comme l'action secondaire qu'elle est. */}
      <label className="galerie bouton bouton--secondaire">
        <Icone nom="image" taille="sm" />
        <span>
          {occupe
            ? interpoler(t.publicationPhotosEnvoi, { n: envoyes + 1, total })
            : t.publicationGalerie}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          multiple
          disabled={occupe}
          onChange={async (evenement) => {
            const fichiers = Array.from(evenement.target.files ?? [])

            if (fichiers.length === 0) {
              return
            }

            setTotal(fichiers.length)
            setEchecs(0)
            setEnvoyes(0)

            let manques = 0

            // UN PAR UN, et non en parallèle. La cible est la 3G d'un téléphone (spec 13) :
            // lancer dix téléversements de front y divise la bande passante sans rien accélérer,
            // et rend le compteur de progression faux — or c'est la seule chose qui dit à
            // l'utilisateur que quelque chose se passe.
            for (const [rang, fichier] of fichiers.entries()) {
              setEnvoyes(rang)

              try {
                const { mediaId } = await televerserMedia(fichier, fichier.name, listingId, null)
                // Remonté DÈS QU'IL ARRIVE, pas à la fin : la vignette apparaît pendant que les
                // suivantes montent, et un échec sur la septième ne fait pas perdre les six
                // premières.
                onCapture(mediaId)
              } catch {
                manques += 1
              }
            }

            setEchecs(manques)
            setEnvoyes(null)
            evenement.target.value = ''
          }}
        />
      </label>

      {echecs > 0 && (
        <p className="galerie__echec" role="status">
          {echecs === 1
            ? 'Une photo n’a pas pu être envoyée. Les autres sont là : réessayez celle-ci seule.'
            : `${echecs} photos n’ont pas pu être envoyées. Les autres sont là : réessayez celles-ci.`}
        </p>
      )}
    </>
  )
}
